import { useEffect, useRef, useState } from 'react';
import { useI18n } from './i18n';
import { startCanvasRecording } from './canvasRecording';
import { getRuntimeAdapter, isMobileBuild } from './platform';

export async function shareCapture(blob, filename) {
  if (isMobileBuild) {
    const adapter = getRuntimeAdapter();
    await (adapter.shareFile || adapter.shareImage)(blob, filename);
    return;
  }
  const file = new File([blob], filename, { type: blob.type });
  // Recorder completion is asynchronous and may no longer have the click's
  // transient activation. A rejected share must still allow video download.
  try {
    if (navigator.userActivation?.isActive !== false && navigator.share && navigator.canShare?.({ files: [file] })) {
      await navigator.share({ files: [file], title: 'KickViz' });
      return;
    }
  } catch (error) {
    if (error.name === 'AbortError') return; // cancelling still discards
  }
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url; link.download = filename;
  document.body.appendChild(link);
  try { link.click(); } finally { link.remove(); }
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}
const button = { background: '#334155', color: '#fff', border: '1px solid #475569', borderRadius: 6, padding: '8px 10px', minHeight: 36, cursor: 'pointer', fontSize: 12 };
export default function CaptureControl({ getCanvas, takeScreenshot, open, setOpen, recordToggleTick = 0, onNotify = null }) {
  const { t } = useI18n();
  const [recording, setRecording] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const session = useRef(null);
  useEffect(() => () => session.current?.cancel(), []);
  // ⇧P from App's keyboard matrix: toggle the recording session. The closure
  // is fresh on the render that carries the bump, so `recording` is current.
  useEffect(() => {
    if (recordToggleTick > 0 && !saving) {
      if (recording) session.current?.stop(); // stopping needs no toast — the Saving/share flow announces itself
      else record();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordToggleTick]);
  const filename = extension => `kickviz_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.${extension}`;
  const report = err => { if (err.name !== 'AbortError') setError(err.message); };
  function record() {
    setError('');
    try {
      session.current = startCanvasRecording(getCanvas(), {
        onComplete: async blob => {
          session.current = null; setRecording(false); setSaving(true); setError('');
          try { await shareCapture(blob, filename(blob.type.includes('mp4') ? 'mp4' : 'webm')); setOpen(false); }
          catch (err) { report(err); } finally { setSaving(false); }
        },
        onError: err => { session.current = null; setRecording(false); report(err); },
      });
      setRecording(true);
      // START is the invisible half (the panel may be closed) — announce it.
      if (onNotify) onNotify(t('cap.recordingToast'));
    } catch (err) { report(err); }
  }
  return <>
    <button type="button" title={recording ? t('cap.stop') : t('cap.hint')} aria-label={t('cap.button')} aria-expanded={open}
      onClick={() => { setOpen(!open); }}
      style={{ ...button, position: 'absolute', bottom: 10, right: 10, zIndex: 32, width: 40, height: 40, padding: 0 }}>
      {recording ? '●' : <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>}
    </button>
    {open && <div style={{ position: 'absolute', bottom: 58, right: 10, zIndex: 250, width: 150, maxHeight: 'calc(var(--kv-viewport-h) - 70px)', overflowY: 'auto', padding: 10, borderRadius: 10, background: '#0f172a', border: '1px solid #475569', display: 'flex', flexDirection: 'column', gap: 6, color: '#fff' }}>
      {recording ? <button style={{ ...button, background: '#991b1b' }} onClick={() => session.current?.stop()}>{t('cap.stop')}</button> : saving ? <span style={{ fontSize: 12 }}>{t('cap.saving')}</span> : <>
        <button style={button} onClick={async () => { setError(''); try { await takeScreenshot(); setOpen(false); } catch (err) { report(err); } }}>{t('cap.screenshot')}</button>
        <button style={button} onClick={record}>{t('cap.record')}</button>
      </>}
      {recording && <span style={{ fontSize: 11 }}>{t('cap.recordingBody')}</span>}
      {error && <span role="alert" style={{ fontSize: 11 }}>{error}</span>}
    </div>}
  </>;
}
